import React from "react";
import { Office } from "common";
import { API } from "taskpane/constants/api";
import {
  CustomProperty,
  LocalStorageItem,
  RoutePath,
  QueryType,
  ConferenceStatusCode,
  StartSessionStatusCode,
} from "taskpane/models/enums";
import {
  IConferenceQueryResult,
  IConferenceAccessSettings,
  IConferenceStatusResponse,
} from "taskpane/models/interfaces";
import { useQueryCreateConference, useQueryUpdateConference } from "taskpane/hooks/queries";
import { useHistory } from "react-router-dom";
import { IUpdateConference } from "taskpane/hooks/queries/useQueryUpdateConference";
import { ICreateConference, ICreateConferenceResponse } from "taskpane/hooks/queries/useQueryCreateConference";
import { useLoading } from "taskpane/providers/loadingProvider";
import "../styles/conferenceHeader.css";
import { getInviteBodyText } from "taskpane/resources";
import { IStartSessionResponse } from "taskpane/hooks/queries/useQueryStartSession";
import { useApiContext } from "taskpane/providers/apiProvider";

interface IConferenceId {
  conferenceId: string;
  setConferenceId: React.Dispatch<React.SetStateAction<string>>;
  errorText: string;
}

export interface IConferenceHeader extends IConferenceAccessSettings, IConferenceId {}

const ConferenceHeader: React.FC<IConferenceHeader> = (props: IConferenceHeader): React.JSX.Element => {
  const history = useHistory();
  const email: string = localStorage.getItem(LocalStorageItem.email);
  const [errorText, setErrorText] = React.useState<string>("");
  const { setLoading } = useLoading();
  const { apiUrl, apiVersion } = useApiContext();
  const { accessMicro, accessVideo, accessDemonstration, accessWaitingHall, conferenceId, setConferenceId } = props;
  const [canCreateConference, setCanCreateConference] = React.useState<boolean>(false);
  const token: string = localStorage.getItem(LocalStorageItem.token);

  const getConferenceSettings = (): IConferenceAccessSettings => {
    return {
      accessMicro: accessMicro,
      accessVideo: accessVideo,
      accessWaitingHall: accessWaitingHall,
      accessDemonstration: accessDemonstration,
    };
  };

  const onClickCreateConference = (): void => {
    setErrorText("");

    const settings: IConferenceAccessSettings = getConferenceSettings();
    const queryCreateConferenceParams: ICreateConference = {
      token,
      email,
      apiUrl,
      apiVersion,
      settings,
    };

    setLoading(true);

    useQueryCreateConference(
      queryCreateConferenceParams,
      (result: IConferenceQueryResult<IStartSessionResponse | ICreateConferenceResponse>) => {
        if (result.errorMessage) {
          setLoading(false);
          return onQueryError(result);
        }

        const response = result.response as ICreateConferenceResponse;
        const { conferenceId, conferenceUrl } = response.results;

        updateBody(conferenceUrl);
        saveConferenceId(conferenceId);
        setConferenceId(conferenceId);
        setLoading(false);
      }
    );
  };

  const saveConferenceId = (conferenceId: string): void => {
    Office.context.mailbox.item.loadCustomPropertiesAsync(
      (asyncResult: Office.AsyncResult<Office.CustomProperties>) => {
        if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
          asyncResult.value.set(CustomProperty.conferenceId, conferenceId);
          asyncResult.value.saveAsync();
        } else {
          setErrorText(`Ошибка при сохранении идентификатора конференции. Текст ошибки: ${asyncResult.error.message}`);
        }
      }
    );
  };

  const onClickUpdateConference = (): void => {
    setErrorText("");

    const settings: IConferenceAccessSettings = getConferenceSettings();
    const queryUpdateConferenceParams: IUpdateConference = {
      token,
      email,
      settings,
      conferenceId,
      apiUrl,
    };

    setLoading(true);

    useQueryUpdateConference(
      queryUpdateConferenceParams,
      (result: IConferenceQueryResult<IStartSessionResponse | IConferenceStatusResponse>) => {
        setLoading(false);

        if (result.errorMessage) {
          return onQueryError(result);
        }
      }
    );
  };
  const onQueryError = (result: IConferenceQueryResult<IStartSessionResponse | IConferenceStatusResponse>): void => {
    const responseErrorMessage: string = result.errorMessage;

    if (result.statusCode !== 200) {
      return setErrorText(responseErrorMessage);
    }

    if (result.queryType === QueryType.startSession) {
      const response = result.response as IStartSessionResponse;
      const statusCode = response.response.statusCode;

      if (statusCode === StartSessionStatusCode.notAuthorized) {
        history.push(RoutePath.loginPage, { errorMessage: responseErrorMessage });
      } else {
        setErrorText(responseErrorMessage);
      }
    } else if ([QueryType.createConference, QueryType.updateConference].includes(result.queryType)) {
      const response = result.response as IConferenceStatusResponse;
      const statusCode = response.status.code;

      if (statusCode === ConferenceStatusCode.notAuthorized) {
        history.push(RoutePath.loginPage, { errorMessage: responseErrorMessage });
      } else {
        setErrorText(responseErrorMessage);
      }
    }
  };

  const getErrorDescription = (errorPlace: string): string => {
    return `Ошибка при попытке вставить текст в ${errorPlace}. Текст ошибки:`;
  };

  const updateBody = (url: string): void => {
    const { getAsync: getBodyAsync, setAsync: setBodyAsync } = Office.context.mailbox.item.body;
    const { getAsync: getSubjectAsync, setAsync: setSubjectAsync } = Office.context.mailbox.item.subject;
    const { setAsync: setLocationAsync } = Office.context.mailbox.item.location;
    const inviteBody: string = getInviteBodyText(url);

    getBodyAsync(Office.CoercionType.Html, {}, (result: Office.AsyncResult<string>) => {
      const error = getErrorDescription("тело письма");

      if (result.status === Office.AsyncResultStatus.Succeeded) {
        const currentBody = result.value || "";
        const closeBodyIndex = currentBody.lastIndexOf("</body>");
        let newBody = inviteBody;

        if (closeBodyIndex > 0) {
          newBody = currentBody.slice(0, closeBodyIndex) + inviteBody + currentBody.slice(closeBodyIndex);
        } else {
          newBody = `${currentBody}${inviteBody}`;
        }

        setBodyAsync(newBody, { coercionType: Office.CoercionType.Html }, (result: Office.AsyncResult<void>) => {
          if (result.status === Office.AsyncResultStatus.Failed) {
            setErrorText((errorText) => {
              errorText = errorText ? `${errorText}\n` : errorText;
              return `${errorText}${error} ${result.error.message}`;
            });
          }
        });
      } else {
        setErrorText((errorText) => {
          errorText = errorText ? `${errorText}\n` : errorText;
          return `${errorText}${error} ${result.error.message}`;
        });
      }
    });

    getSubjectAsync({}, (result: Office.AsyncResult<string>) => {
      const error = getErrorDescription("заголовок письма");

      if (result.status === Office.AsyncResultStatus.Failed) {
        return setErrorText((errorText) => {
          errorText = errorText ? `${errorText}\n` : errorText;
          return `${errorText}${error} ${result.error.message}`;
        });
      }

      const inviteText: string = `${Office.context.mailbox.userProfile.displayName} приглашает Вас присоединиться к конференции VK Teams`;
      const title = result.value ? result.value : inviteText;

      setSubjectAsync(title, {}, (result: Office.AsyncResult<void>) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          setErrorText((errorText) => {
            errorText = errorText ? `${errorText}\n` : errorText;
            return `${errorText}${error} ${result.error.message}`;
          });
        }
      });
    });

    setLocationAsync(url, {}, (result: Office.AsyncResult<void>) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        const error = getErrorDescription("место встречи");

        setErrorText((errorText) => {
          errorText = errorText ? `${errorText}\n` : errorText;
          return `${errorText}${error} ${result.error.message}`;
        });
      }
    });
  };

  React.useEffect(() => {
    const { errorText } = props;

    if (errorText) {
      setErrorText(errorText);
    }
  }, [props.errorText]);

  React.useEffect(() => {
    // Проверка, что мы находимся в окне создания встречи
    const { body, subject, location } = Office.context.mailbox.item;
    const canCreateConf = body != null && subject != null && location != null;

    if (!canCreateConf) {
      setErrorText(`ВНИМАНИЕ! Встречу можно создать только переключившись в режим «Календарь» Outlook.`);
    }

    setCanCreateConference(canCreateConf);
  }, []);

  const isUpdate: boolean = conferenceId && apiVersion >= API.versionWithSettings;
  const buttonTitle: string = isUpdate ? "Обновить" : "Добавить конференцию VK Teams";
  const buttonOnClick = isUpdate ? onClickUpdateConference : onClickCreateConference;
  const description: string = isUpdate
    ? "Можно обновить ссылку на конференцию VK Teams, если изменились параметры встречи"
    : "Нажимая кнопку, ссылка на защищенную конференцию добавляется в поля «Новое событие», «Расположение» и «Описание»";

  return (
    <div className="conferenceHeader">
      <button className="conferenceHeader__buttonConference" onClick={buttonOnClick} disabled={!canCreateConference}>
        {buttonTitle}
      </button>
      {errorText ? (
        <p className="conferenceHeader__error" data-testid="errorText">
          {errorText}
        </p>
      ) : null}
      <p className="conferenceHeader__description">{description}</p>
    </div>
  );
};

export default ConferenceHeader;
